import ChallengeItem from "@/components/core/ChallengeItem"
import { ChallengeStatus, ChallengeType } from "@/components/core/ChallengeItem/ChallengeItem"
import ArticleItem from "@/components/shared/ArticleItem"
import Container from "@/components/shared/Container"
import RoundedButton from "@/components/shared/RoundedButton"
import Typography from "@/components/shared/Typography"
import { ROUTES } from "@/conf"
import { useTranslation } from "next-i18next"
import Image from "next/image"
import { useParams } from "next/navigation"
import { SearchEducationResult } from "swagger/models"
import { GetPlayerComicResult } from "swagger/models/get-player-comic-result"

interface Props {
  cartoonData: GetPlayerComicResult
  educationList: SearchEducationResult[]
  comicsSlug: string
}

const CartoonDetailSection = ({ cartoonData, educationList, comicsSlug }: Props) => {
  const { t } = useTranslation("common")
  const params = useParams()

  const getGameStatus = () => {
    if (cartoonData?.game.claimed === true) return ChallengeStatus.COMPLETED
    else return ChallengeStatus.LOCKED
  }

  const getLocationStatus = () => {
    if (cartoonData?.location.claimed === true) return ChallengeStatus.COMPLETED
    else return ChallengeStatus.LOCKED
  }

  const getBonusQuestionStatus = () => {
    if (cartoonData.location.claimed === true && cartoonData?.bonusQuestion.claimed === true) {
      return ChallengeStatus.COMPLETED
    } else if (cartoonData.location.claimed === true) {
      return ChallengeStatus.ACTIVE
    } else return ChallengeStatus.LOCKED
  }

  const {
    authorName,
    backgroundImageUrl,
    bonusQuestion,
    description,
    dramaturgName,
    educationIds,
    game,
    illustratorName,
    level,
    location,
    name,
    sponsor,
    subtitle,
  } = cartoonData

  return (
    <>
      <div className="absolute left-0 top-0 h-full max-h-[522px] w-full max-w-[944px] before:absolute before:z-10 before:h-full before:w-full before:bg-gradient-to-r before:from-primary-700/0 before:from-0% before:to-primary-700 before:to-100% before:content-['_'] after:absolute after:z-10 after:h-full after:w-full after:bg-gradient-to-b after:from-primary-700/0 after:from-0% after:to-primary-700 after:to-100% after:content-['_'] lg:block lg:max-h-[560px]">
        <Image src={backgroundImageUrl} alt={name} quality={100} sizes="100" fill className="object-cover" />
      </div>
      <Container className="relative z-20 flex flex-col justify-between pb-10 pt-4 lg:flex-row lg:pb-[162px] lg:pt-8">
        <div className="max-w-[600px]">
          <div className="w-fit">
            <RoundedButton
              link={ROUTES.root}
              className="mb-6 lg:mb-8"
              icon={<Image src="/assets/images/arrow_left.svg" width={32} height={32} alt="" />}
            />
          </div>

          <Typography type="h1" tag="h1" className="mb-4">
            {name}
          </Typography>
          <Typography type="l" tag="h1" className="mb-4 uppercase">
            {subtitle}
          </Typography>
          <Typography type="l" className="mb-6 text-primary-100 lg:mb-14">
            {description}
          </Typography>
          <Typography type="caption" className="mb-2 uppercase">
            {t("challenges")}
          </Typography>

          <div className="grid grid-cols-3 gap-4">
            <ChallengeItem
              groshak={game.maxOfGroshak}
              type={ChallengeType.GAME}
              status={getGameStatus()}
              comicsSlug={comicsSlug}
            />

            <ChallengeItem
              groshak={location.maxOfGroshak}
              type={ChallengeType.LOCATION}
              status={getLocationStatus()}
              comicsSlug={comicsSlug}
            />

            <ChallengeItem
              groshak={bonusQuestion.maxOfGroshak}
              type={ChallengeType.BONUS}
              status={getBonusQuestionStatus()}
              comicsSlug={comicsSlug}
            />
          </div>
        </div>

        <div className="w-full divide-y divide-primary-500 pt-10 *:py-4 lg:max-w-sm">
          <div>
            <Typography type="h2" tag="h2" className="mb-2">
              {t("moreAboutCartoon")}
            </Typography>
            <Typography type="l" className="text-primary-200">
              {t("exploreHistory")}
            </Typography>
          </div>

          <div className="flex justify-between">
            <div>
              <Typography type="caption" className="mb-2 uppercase">
                {t("cartoonLocation")}
              </Typography>
              <Typography type="l" className="text-primary-200">
                {location.name}
              </Typography>
            </div>

            <RoundedButton
              link={`${ROUTES.map}?cartoon=${params.slug}`}
              icon={<Image src="/assets/images/location.svg" width={24} height={24} alt="" />}
            />
          </div>

          <div>
            <Typography type="caption" className="mb-2 uppercase">
              {t("authors")}
            </Typography>
            <Typography type="l" className="text-primary-200">
              {t("authorName")}: {authorName}
              <br />
              {t("dramaturgName")}: {dramaturgName}
              <br />
              {t("illustratorName")}: {illustratorName}
            </Typography>
          </div>

          {sponsor && (
            <div>
              <Typography type="caption" className="mb-2 uppercase">
                {t("partners")}
              </Typography>
              <Typography type="l" className="text-primary-200">
                {sponsor}
              </Typography>
            </div>
          )}

          {educationIds.length > 0 && (
            <div>
              <Typography type="caption" className="mb-2 uppercase">
                {t("more")}
              </Typography>
              {educationList.map((e) => (
                <ArticleItem
                  key={e.educationId}
                  link={e.slug}
                  title={e.title}
                  subtitle={e.subtitle}
                  imageSrc={e.imageUrl}
                />
              ))}
            </div>
          )}
        </div>
      </Container>
    </>
  )
}

export default CartoonDetailSection
